import { AttachmentSelector } from '@containers/AttachmentSelector'
import { PermissionSet, usePermission } from '@context'
import { AttachmentExemptIcon } from './AttachmentExemptIcon'
import React from 'react'
import { Control, FieldPath, FieldValues, useController } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

interface Props {
  attachmentIds: number[]
  onAttachmentIdsChange: (ids: number[]) => void
  attachmentExempt: boolean
  onAttachmentExemptChange?: (value: boolean) => void
  disabled?: boolean
  exemptionDisabled?: boolean
  showUndo?: boolean
  showExempt?: boolean
}

export const DocumentAttachmentsField: React.FC<Props> = ({
  attachmentIds,
  onAttachmentIdsChange,
  attachmentExempt,
  onAttachmentExemptChange,
  disabled = false,
  exemptionDisabled,
  showUndo = true,
  showExempt = true
}) => {
  const { t } = useTranslation()
  const canChangeExemption = usePermission(PermissionSet.BOOKKEEPING_EDIT) && !exemptionDisabled
  const hasAttachments = attachmentIds.length > 0
  const showPicker = !attachmentExempt || hasAttachments
  const showStatus = attachmentExempt && !hasAttachments

  return (
    <FieldColumn>
      {showStatus && (
        <StatusRow>
          <AttachmentExemptIcon aria-hidden size={12} />
          {t('document.attachmentExempt')}
          {showUndo && canChangeExemption && (
            <UndoButton
              type="button"
              onClick={() => onAttachmentExemptChange?.(false)}
              onMouseDown={event => event.stopPropagation()}
              onPointerDown={event => event.stopPropagation()}
            >
              {t('document.attachmentExemptUndo')}
            </UndoButton>
          )}
        </StatusRow>
      )}
      {showPicker && (
        <AttachmentSelector
          value={attachmentIds}
          onChange={onAttachmentIdsChange}
          showRemoveInsteadOfViewLink={true}
          disabled={disabled}
        />
      )}
      {showExempt && canChangeExemption && !attachmentExempt && !hasAttachments && (
        <ExemptButton
          type="button"
          onClick={() => onAttachmentExemptChange?.(true)}
          onMouseDown={event => event.stopPropagation()}
          onPointerDown={event => event.stopPropagation()}
        >
          <AttachmentExemptIcon aria-hidden size={12} />
          {t('document.attachmentExempt')}
        </ExemptButton>
      )}
    </FieldColumn>
  )
}

export function ControlledDocumentAttachmentsField<T extends FieldValues>({
  control,
  disabled
}: {
  control: Control<T>
  disabled?: boolean
}) {
  const attachmentIdsField = useController({
    name: 'attachment_ids' as FieldPath<T>,
    control
  })
  const attachmentExemptField = useController({
    name: 'attachment_exempt' as FieldPath<T>,
    control
  })

  return (
    <DocumentAttachmentsField
      attachmentIds={(attachmentIdsField.field.value as number[] | undefined) ?? []}
      onAttachmentIdsChange={attachmentIdsField.field.onChange}
      attachmentExempt={Boolean(attachmentExemptField.field.value)}
      onAttachmentExemptChange={attachmentExemptField.field.onChange}
      disabled={disabled}
    />
  )
}

const FieldColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  align-items: flex-start;
  width: 100%;
`

const ExemptButton = styled.button`
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  padding: 6px 10px;
  border: none;
  border-radius: 0.6rem;
  background: transparent;
  cursor: pointer;
  font: inherit;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.metalGray};

  svg {
    width: 12px;
    height: 12px;
    flex-shrink: 0;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.neutralGray};
  }

  &:active {
    background: ${({ theme }) => theme.colors.neutralGray};
    filter: brightness(90%);
  }
`

const StatusRow = styled.div`
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  margin: 0;
  padding: 0;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.metalGray};

  svg {
    width: 12px;
    height: 12px;
    flex-shrink: 0;
  }
`

const UndoButton = styled.button`
  flex-shrink: 0;
  margin: 0;
  padding: 4px 8px;
  border: none;
  border-radius: 0.6rem;
  background: transparent;
  cursor: pointer;
  font: inherit;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.metalGray};

  &:hover {
    background: ${({ theme }) => theme.colors.neutralGray};
  }
`
